import { LoadingOutlined } from '@ant-design/icons'
import { ApolloClient } from '@apollo/client'
import {
  AuthGroups,
  createApolloClient,
  fetchAllProjectServices,
  fetchMeUser,
  getAccessToken,
  type RemoteModulesInfo,
} from 'admin-web-utils'
import React, { useCallback, useContext, useEffect, useMemo } from 'react'
import { useAsyncFn, useLocalStorage } from 'react-use'
import { Project, Service, UserWithoutGroups } from '../../api'
import { SimplePage } from '../../components/SimplePage'
import { fetchRemoteModulesManifest } from '../../remote-modules'
import { useAppContext } from '../App'

export type Me = UserWithoutGroups & {
  authGroups: AuthGroups // from Cognito token
  projectServices: Record<string, Service[]>
}

export type ProjectInfo = {
  meUser: Me
  navProject: Project | undefined
  remotes: RemoteModulesInfo
}

export interface ConsoleState {
  customerClient: ApolloClient<unknown>
  me: Me
  navProject: Project | undefined
  remotes: RemoteModulesInfo
  getAccessToken: () => Promise<string>
  refreshData: () => Promise<void>
  setNavProjectId: (projectId: string) => void
}

const Context = React.createContext<ConsoleState>(undefined as never)

export const useConsoleContext = (): ConsoleState => useContext(Context)

export const ConsoleContext: React.FC = (props) => {
  const { environment, cognitoUser, setCognitoUser } = useAppContext()

  if (!cognitoUser) {
    throw new Error(
      'Cannot load Console container. Cognito user must be available.',
    )
  }

  /**
   * Callback:
   * Gets a valid access token and updates
   * cognito user if a refresh occurred.
   */
  const accessTokenCallback = useCallback(async () => {
    const token = await getAccessToken({
      cognitoUser,
      onCognitoUserChanged: setCognitoUser,
    })
    return token.getJwtToken()
  }, [cognitoUser, setCognitoUser])

  /**
   * Memo:
   * Creates an Apollo client to access customer-level API
   */
  const customerClient: ApolloClient<unknown> = useMemo(() => {
    return createApolloClient(environment.customerApiUri, accessTokenCallback)
  }, [environment, accessTokenCallback])

  /**
   * LocalStorage state:
   * Last navigation project
   */
  const [navProjectId, setNavProjectId] = useLocalStorage<string>(
    'navigation-project-id',
  )

  /**
   * Async:
   * Fetches user and auth info
   */
  const [initData, fetchInitData] =
    useAsyncFn(async (): Promise<ProjectInfo> => {
      const [meUser, token] = await Promise.all([
        fetchMeUser(customerClient),
        getAccessToken({
          cognitoUser,
          forceRefresh: true,
          onCognitoUserChanged: () => undefined,
        }),
      ])
      const projectServices = await fetchAllProjectServices(
        meUser.projects,
        accessTokenCallback,
      )
      const authGroups = token.decodePayload()['cognito:groups'] as string[]

      const navProject = navProjectId
        ? meUser.projects.find((p) => p.id === navProjectId)
        : meUser.projects[0]

      const remotes = await fetchRemoteModulesManifest(navProject?.name)

      const result = {
        meUser: {
          ...meUser,
          authGroups,
          projectServices,
        },
        navProject,
        remotes,
      }

      return result
    }, [customerClient, cognitoUser, navProjectId])

  /**
   * Callback:
   * Refreshes user info
   */
  const refreshData = useCallback(async () => {
    fetchInitData()
  }, [fetchInitData])

  /**
   * Effect:
   * Load user info and react to nav project changes
   */
  useEffect(() => {
    refreshData()
  }, [refreshData])

  if (initData.error) {
    return <SimplePage>An error occurred loading the console.</SimplePage>
  } else if (!initData.value) {
    return (
      <SimplePage>
        <LoadingOutlined />
      </SimplePage>
    )
  } else {
    return (
      <Context.Provider
        value={{
          me: initData.value.meUser,
          customerClient,
          navProject: initData.value.navProject,
          refreshData,
          getAccessToken: accessTokenCallback,
          setNavProjectId,
          remotes: initData.value.remotes,
        }}
      >
        {props.children}
      </Context.Provider>
    )
  }
}
